import moment from 'moment';
import { useEffect, useState, useRef } from 'react';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import 'primeflex/primeflex.css';
import { InputSwitch } from 'primereact/inputswitch';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { classes } from './pricing.st.css';

import PLAN_FEATURES from './plan-feature-list.json';

import { Box, Loader, Heading } from 'wix-style-react';
import {
  fetchPlans,
  fetchSubscriptionApi,
  selectPlans,
  selectSubscription,
  fetchTicketPacks,
  selectTicketPacks,
  selectTicketPacksLoading,
} from '../../features/site-slice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { API_INSTANCE } from '../../features/utils';
import { selectSiteData } from '../../features/account-slice';
import { useSelector } from 'react-redux';
import { CancelModal } from './canceling-modal';
import { capitalize } from 'lodash';
import { BackButton } from 'src/redesign/components/BackButton';
import { useHistory } from 'react-router-dom';
import { getSelectedSiteDataSelector } from 'src/redesign/features/SelectedSite/store/selectors';
import { API_URL } from 'src/utils/transport';
import { SwitchButton } from 'src/redesign/components/SwitchButton';
import { selectPlanDetails } from 'src/features/settings-slice';
import { Button } from 'src/redesign/components/Button';
import { TicketPackCard, ActivePackBalances, SubscriptionStatusBanner } from './components';
import { PlanRecommendationModal } from './components/PlanRecommendationModal';

export const nameMapping = {
  featured: 'professional',
};

const urlSearchParams = new URLSearchParams(window.location.search);
let instance = urlSearchParams.get('instance');

if (!instance) {
  const hashFragment = window.location.hash.split('?')[1] || '';
  const hashParams = new URLSearchParams(hashFragment);
  instance = hashParams.get('instance');
}

const PlanCard = ({ plan = {}, plans, isYearly, subscription, siteData, couponCode }) => {
  // Check if this is the current plan
  const isCurrentPlan = plan.billing_type === subscription?.billing_type;

  return (
    <div
      className="p-2"
      style={{ flex: '1 1 0', minWidth: '210px', maxWidth: '320px' }}
      key={plan.name}
    >
      <div className="h-full">
        <div
          className={`shadow-2 p-3 h-full flex flex-column surface-card relative ${
            isCurrentPlan ? 'border-3 border-green-500' : ''
          }`}
          data-plan={plan.billing_type}
          style={{
            borderRadius: '6px',
            backgroundColor: isCurrentPlan ? 'rgba(34, 197, 94, 0.05)' : '',
          }}
        >
          {isCurrentPlan ? (
            <div
              className="bg-green-500 text-0 font-bold text-sm inline-block p-2 absolute"
              style={{ borderRadius: 20, margin: '0 auto', top: -20 }}
            >
              CURRENT PLAN
            </div>
          ) : plan.billing_type === 'business' ? (
            <div
              className="bg-blue-500 text-0 font-bold text-sm inline-block p-2 absolute"
              style={{ borderRadius: 20, margin: '0 auto', top: -20 }}
            >
              MOST POPULAR
            </div>
          ) : null}

          <div className="text-900 font-medium text-2xl mb-2">{plan.name}</div>
          <div className="text-600 text-sm" style={{ minHeight: '48px' }}>
            {plan.description}
          </div>
          <hr className="my-3 mx-0 border-top-1 border-none surface-border" />
          <div style={{ minHeight: '120px' }}>
            {plan.billing_type === 'free' ? (
              <>
                <div className="text-xl font-semibold text-900">Free Forever</div>
                <div className="text-600 mt-2">{plan.subtitle}</div>
              </>
            ) : plan.monthly_price === null || plan.monthly_price === undefined ? (
              <>
                <div className="flex align-items-center">
                  <span className="font-bold text-2xl text-900">Custom Pricing</span>
                </div>
                <div className="flex align-items-center mt-2">
                  <span className="font-semibold text-700 text-base">
                    For platforms, marketplaces, and large organizations
                  </span>
                </div>
                <div className="flex align-items-center mt-1">
                  <span className="font-light font-italic text-600 text-sm">
                    Contact TicketSpot for pricing
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex align-items-center">
                  <span className="font-bold text-2xl text-900">
                    ${isYearly ? plan.yearly_price : plan.monthly_price}
                  </span>
                  <span className="ml-2 font-medium text-600">per month</span>
                </div>

                <div className="flex align-items-center mt-2">
                  <span className="font-semibold text-900 text-base">{plan.subtitle}</span>
                </div>

                <div className="flex align-items-center mt-1">
                  <span className="font-light font-italic text-600 text-sm">
                    billed {isYearly ? 'yearly' : 'monthly'}
                  </span>
                </div>
              </>
            )}
          </div>
          <hr className="my-3 mx-0 border-top-1 border-none surface-border" />

          {/* Key Plan Information - Prominent Display */}
          <div style={{ minHeight: 'auto' }}>
            {plan.billing_type === 'free' ? (
              <>
                {/* Free Tickets Only */}
                <div className="mb-3 p-3 bg-orange-50 border-round border-2 border-orange-200">
                  <div className="flex align-items-center justify-content-center">
                    <i
                      className="pi pi-info-circle mr-2 text-orange-600"
                      style={{ fontSize: '1.1rem' }}
                    ></i>
                    <span className="font-semibold text-900">
                      Free tickets only (no paid tickets)
                    </span>
                  </div>
                </div>
              </>
            ) : plan.name === 'Enterprise' ? (
              <>
                {/* Enterprise Custom Pricing */}
                <div className="mb-3 p-3 bg-purple-50 border-round border-2 border-purple-200">
                  <div className="text-center">
                    <i
                      className="pi pi-briefcase text-purple-600"
                      style={{ fontSize: '1.5rem' }}
                    ></i>
                    <div className="font-semibold text-900 mt-2">
                      Multi-organizer platforms & marketplaces
                    </div>
                    <div className="text-600 text-sm mt-1">
                      Custom volume agreements & infrastructure
                    </div>
                  </div>
                </div>
              </>
            ) : plan.name === 'Business+' ? (
              <>
                {/* Business+ Branding Focus - Enhanced */}
                <div className="mb-3 p-3 bg-green-50 border-round border-2 border-green-200">
                  <div className="text-center">
                    <i
                      className="pi pi-eye-slash text-green-600"
                      style={{ fontSize: '1.5rem' }}
                    ></i>
                    <div className="font-bold text-900 mt-2 text-lg">Fully Branded Experience</div>
                  </div>
                  <div className="mt-3 text-left">
                    <div className="flex align-items-center gap-2 mb-2">
                      <span style={{ color: '#f59e0b', fontSize: '16px' }}>⭐</span>
                      <span className="font-semibold text-900 text-sm">
                        Remove all TicketSpot branding
                      </span>
                    </div>
                    <div className="flex align-items-center gap-2 mb-2">
                      <span style={{ color: '#f59e0b', fontSize: '16px' }}>⭐</span>
                      <span className="font-semibold text-900 text-sm">Use your own domain</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                      <span style={{ color: '#f59e0b', fontSize: '16px' }}>⭐</span>
                      <span className="font-semibold text-900 text-sm">
                        Fully branded ticketing & checkout experience
                      </span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <></>
            )}

            {plan.name === 'Enterprise' && false && (
              <div className="mb-3 p-3 bg-purple-50 border-round">
                <div className="flex align-items-center mb-2">
                  <i
                    className="pi pi-chart-bar mr-2 text-purple-600"
                    style={{ fontSize: '1.2rem' }}
                  ></i>
                  <span className="font-semibold text-900">Select Ticket Volume</span>
                </div>
                <Dropdown
                  value={selectedQuota}
                  onChange={(e) => handleQuotaChange(e.value)}
                  options={volumnOptions}
                  optionLabel="displayName"
                  className="w-full"
                  checkmark={true}
                  highlightOnSelect={false}
                  placeholder="Choose your volume"
                  pt={{
                    root: { className: 'border-2 border-purple-300' },
                    input: { className: 'font-semibold text-900' },
                  }}
                />
                <small className="text-600 mt-1 block">
                  Up to {selectedQuota?.name} tickets per month
                </small>
              </div>
            )}
          </div>
          <ul className="list-none p-0 m-0 flex-grow-1">
            {plan?.benefits?.map((benefit, benefitIndex) => (
              <li className="flex align-items-center mb-3" key={benefitIndex}>
                <i className="pi pi-check-circle text-green-500 mr-2"></i>
                <span className="text-900">{benefit.name}</span>
              </li>
            ))}
          </ul>
          <hr className="mb-3 mx-0 border-top-1 border-none surface-border mt-auto" />

          <form
            action={
              window.brand === 'wix'
                ? `${API_URL}/api/v1/dashboard/billing/wix/sessions?instance=${instance}&brand=wix`
                : `${API_URL}/api/v1/dashboard/billing/sessions`
            }
            method="POST"
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            {plan.monthly_price !== null && plan.monthly_price !== undefined && (
              <>
                <input
                  type="hidden"
                  name="priceId"
                  value={isYearly ? plan.yearly_price_id : plan.monthly_price_id}
                />
                <input type="hidden" name="billingType" value={plan.billing_type} />
                <input type="hidden" name="isYearly" value={isYearly} />
                {couponCode && <input type="hidden" name="couponName" value={couponCode} />}
              </>
            )}

            <SubscriptionComponent
              subscription={subscription}
              plans={plans}
              plan={plan}
              siteData={siteData}
            />
          </form>
        </div>
      </div>
    </div>
  );
};

const PricingPlans = ({ plans, isYearly, subscription, siteId, siteData, couponCode }) => {
  return (
    <>
      {plans.map((plan) => (
        <PlanCard
          plan={plan}
          plans={plans}
          isYearly={isYearly}
          siteId={siteId}
          siteData={siteData}
          subscription={subscription}
          couponCode={couponCode}
          key={plan.billing_type}
        />
      ))}
    </>
  );
};

export const Pricing = () => {
  const site = useSelector(getSelectedSiteDataSelector);

  const siteId = site?.id;
  const [isLoading, setLoading] = useState(true);
  const [isYearly, setIsYearly] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<{
    value?: string;
    planName?: string;
    currency?: string;
    transactionId?: string;
    isPack?: boolean;
  }>({});
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  // NEW: View toggle state
  const [view, setView] = useState<'subscriptions' | 'packs'>('subscriptions');
  const [showPlanRecommendationModal, setShowPlanRecommendationModal] = useState(false);
  const history = useHistory();
  const toast = useRef<Toast>(null);

  const siteData = useSelector(selectSiteData);

  const subscription = useAppSelector(selectSubscription);
  const plans = useAppSelector(selectPlans);
  // NEW: Ticket pack selectors
  const ticketPacks = useAppSelector(selectTicketPacks);
  const ticketPacksLoading = useAppSelector(selectTicketPacksLoading);
  const planDetails = useAppSelector(selectPlanDetails);

  const dispatch = useAppDispatch();

  // Extract wix-trial and discount_reason from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const hashFragment = window.location.hash.split('?')[1] || '';
  const hashParams = new URLSearchParams(hashFragment);

  const isWixTrial =
    urlParams.get('wix-trial') === 'true' || hashParams.get('wix-trial') === 'true';
  const discountReason =
    urlParams.get('discount_reason') || hashParams.get('discount_reason') || '';

  // Calculate free trial status and days remaining
  const isInFreeTrial =
    site?.billing_type === 'trial' &&
    site?.free_trial_at &&
    moment().diff(moment(site.free_trial_at), 'days') < 7;
  const trialDaysRemaining = isInFreeTrial
    ? 7 - moment().diff(moment(site.free_trial_at), 'days')
    : 0;

  useEffect(() => {
    async function fetchData() {
      await dispatch(fetchPlans());
      await dispatch(fetchTicketPacks()); // NEW: Fetch ticket packs
      dispatch(fetchSubscriptionApi(siteId));
      setLoading(false);
    }
    if (siteId) {
      fetchData();
    }
  }, [dispatch, siteId]);

  useEffect(() => {
    // Check both URL search params and hash fragment for error/success parameters
    const urlParams = new URLSearchParams(window.location.search);
    const hashFragment = window.location.hash.split('?')[1] || '';
    const hashParams = new URLSearchParams(hashFragment);

    const hasError = urlParams.get('error') === 'true' || hashParams.get('error') === 'true';
    const hasSuccess = urlParams.get('success') === 'true' || hashParams.get('success') === 'true';

    if (hasError) {
      setShowErrorModal(true);

      // Remove the error parameter from both URL and hash fragment
      if (urlParams.has('error')) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('error');
        window.history.replaceState({}, '', newUrl);
      }

      if (hashParams.has('error')) {
        hashParams.delete('error');
        const newHashFragment = hashParams.toString();
        const hashBase = window.location.hash.split('?')[0];
        const newHash = newHashFragment ? `${hashBase}?${newHashFragment}` : hashBase;
        window.history.replaceState(
          {},
          '',
          window.location.pathname + window.location.search + newHash,
        );
      }
    }
    if (hasSuccess) {
      // Extract success data from URL params
      const value = urlParams.get('value') || hashParams.get('value');
      const planName = urlParams.get('plan_name') || hashParams.get('plan_name');
      const currency = urlParams.get('currency') || hashParams.get('currency');
      const transactionId = urlParams.get('transaction_id') || hashParams.get('transaction_id');
      const packPurchase = urlParams.get('pack_purchase') || hashParams.get('pack_purchase');

      setSuccessData({
        value,
        planName,
        currency,
        transactionId,
        isPack: packPurchase === 'success',
      });
      setShowSuccessModal(true);

      // Track conversion in Google Analytics / GTM
      if (window.dataLayer && value) {
        window.dataLayer.push({
          event: 'purchase',
          purchase_type: packPurchase === 'success' ? 'ticket_pack' : 'subscription',
          transaction_id: transactionId || undefined,
          value: parseFloat(value),
          currency: currency || 'USD',
          plan_name: planName || undefined,
        });
      }

      // Remove the success parameters from both URL and hash fragment
      const paramsToRemove = [
        'success',
        'value',
        'plan_name',
        'currency',
        'transaction_id',
        'pack_purchase',
      ];

      if (urlParams.has('success')) {
        const newUrl = new URL(window.location.href);
        paramsToRemove.forEach((param) => newUrl.searchParams.delete(param));
        window.history.replaceState({}, '', newUrl);
      }

      if (hashParams.has('success')) {
        paramsToRemove.forEach((param) => hashParams.delete(param));
        const newHashFragment = hashParams.toString();
        const hashBase = window.location.hash.split('?')[0];
        const newHash = newHashFragment ? `${hashBase}?${newHashFragment}` : hashBase;
        window.history.replaceState(
          {},
          '',
          window.location.pathname + window.location.search + newHash,
        );
      }
    }
  }, []);

  if (!subscription || isLoading) {
    return (
      <Box align="center">
        <Loader />
      </Box>
    );
  }

  const handleCancel = (e) => {
    e.preventDefault();
    setVisible(true); // Show the confirm dialog
  };

  const handleWixCancel = (e) => {
    e.preventDefault();
    window.open(
      'https://premium.wix.com/wix/api/mpContainerStaticController?originWidgetName=billings',
      '_blank',
    );
  };

  const confirmCancel = async () => {
    setShowCancelModal(true);
    await API_INSTANCE.delete(`/api/v1/dashboard/billing/shopify/cancel`);
  };

  return (
    <Box direction="vertical">
      <Toast ref={toast} />
      <Box marginTop={2}>
        <BackButton onClick={() => history.push(`/events`)}>Back to Events</BackButton>
      </Box>
      {showCancelModal && <CancelModal onSubmit={() => dispatch(fetchSubscriptionApi(siteId))} />}
      <ConfirmDialog
        visible={visible}
        onHide={() => setVisible(false)}
        message="Are you sure you want to cancel your subscription? You can resubscribe at any time."
        header="Confirm Cancellation"
        icon="pi pi-exclamation-triangle"
        acceptClassName="p-button-danger"
        accept={confirmCancel}
        reject={() => setVisible(false)}
      />

      {/* Error Modal for upgrade failures */}
      <Dialog
        header="Upgrade Error"
        visible={showErrorModal}
        style={{ width: '450px' }}
        onHide={() => setShowErrorModal(false)}
        draggable={false}
        resizable={false}
      >
        <div className="flex flex-column gap-3">
          <div className="flex align-items-center gap-2">
            <i
              className="pi pi-exclamation-triangle text-orange-500"
              style={{ fontSize: '1.5rem' }}
            ></i>
            <span className="font-medium">There was an issue upgrading your account.</span>
          </div>
          <p className="m-0 text-600">
            We encountered a problem while processing your upgrade. Please try again or contact our
            support team through the live chat for immediate assistance.
          </p>
        </div>
      </Dialog>

      {/* Success Modal for purchases */}
      <Dialog
        header={successData.isPack ? 'Ticket Pack Purchased!' : 'Successfully Subscribed!'}
        visible={showSuccessModal}
        style={{ width: '500px' }}
        onHide={() => {
          setShowSuccessModal(false);
          // Refresh subscription data
          dispatch(fetchSubscriptionApi(siteId));
          dispatch(fetchTicketPacks());
        }}
        draggable={false}
        resizable={false}
      >
        <div className="flex flex-column gap-4">
          <div className="flex align-items-center justify-content-center">
            <i className="pi pi-check-circle text-green-500" style={{ fontSize: '4rem' }}></i>
          </div>
          <div className="text-center">
            <p className="m-0 text-900 font-semibold text-lg mb-3">
              {successData.isPack
                ? 'Your ticket pack has been successfully purchased and added to your account.'
                : 'Your subscription has been activated. You now have access to all premium features.'}
            </p>
            {successData.planName && (
              <p className="m-0 text-600">
                Plan: <span className="font-semibold">{successData.planName}</span>
              </p>
            )}
          </div>
        </div>
      </Dialog>
      <Box>
        <div className="py-8 bg-white" style={{ width: '100%' }}>
          <div className="text-900 font-bold text-6xl mb-4 text-center">Choose Your Plan</div>

          {/* Killer Differentiator - No Fees Message */}
          <div className="text-center mb-4">
            <div
              className="font-bold text-4xl mb-2"
              style={{ color: 'var(--brand-background-color)' }}
            >
              No ticket fees. No revenue share.
            </div>
            <div className="font-semibold text-2xl text-900">
              You keep 100% of your ticket sales.
            </div>
          </div>

          <div className="text-700 text-xl mb-6 text-center line-height-3">
            Only pay a simple monthly plan.
          </div>

          {/* NEW: Plan Recommendation Trigger Button */}
          <div className="flex justify-content-center mb-5">
            <Button
              onClick={() => setShowPlanRecommendationModal(true)}
              priority="secondary"
              size="large"
              style={{
                borderColor: 'var(--brand-background-color)',
                color: 'var(--brand-background-color)',
                backgroundColor: 'transparent',
                fontWeight: '600',
                padding: '12px 32px',
              }}
            >
              <i className="pi pi-question-circle mr-2" />
              Find the Right Plan
            </Button>
          </div>

          <div className={`mb-6 ${classes.summary}`}>
            <SubscriptionStatusBanner
              subscription={subscription}
              site={site}
              siteId={siteId}
              isWixTrial={isWixTrial}
              discountReason={discountReason}
              isInFreeTrial={isInFreeTrial}
              trialDaysRemaining={trialDaysRemaining}
              handleCancel={handleCancel}
              planDetails={planDetails}
            />
          </div>

          {/* Monthly/Yearly Toggle - Moved Above */}
          <div className="flex align-items-center justify-content-center text-900 mb-1 mt-5">
            <span className="font-semibold mr-3">Monthly</span>
            <InputSwitch checked={isYearly} onChange={(e) => setIsYearly(e.value)} />
            <span className="ml-3">Yearly</span>
          </div>
          {!isInFreeTrial && subscription?.billing_type !== 'free' && (
            <div className="flex align-items-center justify-content-center text-500 mb-4">
              <span className="bg-orange-100 500 text-orange-500 font-semibold px-2 py-1 border-round">
                Save 25% with annual billing
              </span>
            </div>
          )}

          {/* Subscription/Packs Toggle */}
          <div className="flex justify-content-center mb-4">
            <div style={{ width: '400px' }}>
              <SwitchButton
                items={['Subscriptions', 'Ticket Packs']}
                value={view === 'subscriptions' ? 'Subscriptions' : 'Ticket Packs'}
                onChange={(value) => setView(value === 'Subscriptions' ? 'subscriptions' : 'packs')}
                key="ticket-packs"
              />
            </div>
          </div>

          {/* NEW: Info banner for one-time events (show when viewing subscriptions) */}
          {/* {view === 'subscriptions' && (
            <div
              className="mb-4 p-4 border-round flex align-items-center gap-3 mx-auto"
              style={{
                backgroundColor: '#f5f3ff',
                border: '2px solid #e9d5ff',
                maxWidth: '800px',
              }}
            >
              <i
                className="pi pi-ticket text-2xl"
                style={{ color: 'var(--brand-background-color)' }}
              />
              <div className="flex-grow-1" style={{ color: '#5a6c7d' }}>
                <strong style={{ color: '#2c3e50' }}>Planning a one-time event?</strong> Ticket
                Packs are perfect for you - no recurring subscription needed, just pay once and go!
              </div>
              <Button
                onClick={() => setView('packs')}
                priority="secondary"
                size="medium"
                style={{
                  color: 'var(--brand-background-color)',
                  borderColor: 'var(--brand-background-color)',
                  backgroundColor: 'transparent',
                  whiteSpace: 'nowrap',
                }}
              >
                View Packs
              </Button>
            </div>
          )} */}

          {view === 'subscriptions' ? (
            <>
              {/* Subscriptions View */}
              {/* Coupon Code Section */}
              <div className="flex flex-column align-items-center justify-content-center mb-6 mt-4">
                {!showCouponInput ? (
                  <button
                    type="button"
                    onClick={() => setShowCouponInput(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: '13px',
                      fontWeight: '400',
                      padding: '4px 8px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'var(--brand-background-color)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#6b7280';
                    }}
                  >
                    Apply coupon
                  </button>
                ) : (
                  <div
                    className="flex align-items-center gap-2"
                    style={{
                      animation: 'fadeIn 0.3s ease-in',
                    }}
                  >
                    <div className="p-inputgroup" style={{ maxWidth: '300px' }}>
                      <span
                        className="p-inputgroup-addon"
                        style={{
                          backgroundColor: '#f8f4fc',
                          borderColor: 'var(--brand-background-color)',
                        }}
                      >
                        <i
                          className="pi pi-tag"
                          style={{ color: 'var(--brand-background-color)' }}
                        ></i>
                      </span>
                      <input
                        type="text"
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.trim())}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid var(--brand-background-color)',
                          borderRadius: '0 4px 4px 0',
                          fontSize: '14px',
                          outline: 'none',
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#7c1dae';
                          e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(157, 37, 220, 0.25)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'var(--brand-background-color)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                    {couponCode && (
                      <div
                        className="flex align-items-center gap-1 bg-green-100 px-2 py-1 border-round"
                        style={{ animation: 'fadeIn 0.2s ease-in' }}
                      >
                        <i
                          className="pi pi-check-circle text-green-600"
                          style={{ fontSize: '14px' }}
                        ></i>
                        <span className="text-green-700 text-sm font-semibold">Applied</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setShowCouponInput(false);
                        setCouponCode('');
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#6c757d',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      title="Close"
                    >
                      <i className="pi pi-times" style={{ fontSize: '14px' }}></i>
                    </button>
                  </div>
                )}
              </div>

              <div
                className="flex flex-wrap justify-content-center gap-2"
                style={{ maxWidth: '1800px', margin: '0 auto' }}
              >
                <PricingPlans
                  plans={plans}
                  isYearly={isYearly}
                  subscription={subscription}
                  siteId={siteId}
                  siteData={site}
                  couponCode={couponCode}
                />
              </div>
            </>
          ) : (
            <>
              {/* Packs View */}
              <ActivePackBalances planDetails={planDetails} />

              {/* Warning banner for free plan and trial users */}
              {(subscription?.billing_type === 'free' ||
                subscription?.billing_type === 'trial') && (
                <div
                  className="mb-4 p-4 border-round mx-auto"
                  style={{
                    backgroundColor: '#fff8e1',
                    border: '2px solid #ffd54f',
                    maxWidth: '800px',
                  }}
                >
                  <strong style={{ color: '#2c3e50' }}>Subscription Required</strong>
                  <br />
                  <span style={{ color: '#5a6c7d' }}>
                    To purchase ticket packs, you need an active paid subscription plan.
                  </span>
                  <Button
                    onClick={() => setView('subscriptions')}
                    priority="secondary"
                    size="medium"
                    className="mt-2"
                    style={{
                      color: 'var(--brand-background-color)',
                      borderColor: 'var(--brand-background-color)',
                      backgroundColor: 'transparent',
                    }}
                  >
                    View Plans
                  </Button>
                </div>
              )}

              {/* Pack cards grid */}
              <div className="grid">
                {ticketPacks?.map((pack) => (
                  <TicketPackCard
                    key={pack.pack_id}
                    pack={pack}
                    isLoading={false}
                    hasActiveSubscription={
                      subscription?.billing_type !== 'free' &&
                      subscription?.billing_type !== 'trial'
                    }
                  />
                ))}
              </div>

              {/* Empty state */}
              {!ticketPacksLoading && (!ticketPacks || ticketPacks.length === 0) && (
                <div className="text-center py-8">
                  <i className="pi pi-inbox" style={{ fontSize: '4rem', color: '#9ca3af' }} />
                  <div className="text-xl mt-3 text-600">No ticket packs available</div>
                </div>
              )}
            </>
          )}
        </div>
      </Box>

      {/* Feature comparison - only show for subscriptions view */}
      {view === 'subscriptions' && (
        <>
          <Box align="center" background={'#F9FAFB'}>
            <Heading appearance="H2" style={{ textDecoration: 'underline' }}>
              Compare Plans and Features
            </Heading>
          </Box>
          <Box>
            <div className="surface-ground px-4 py-8 md:px-6 lg:px-8" style={{ width: '100%' }}>
              <table
                className="w-full white-space-nowrap"
                style={{
                  borderCollapse: 'collapse',
                  borderSpacing: '0',
                  background: 'white',
                }}
              >
                <thead>
                  <tr>
                    <td className="surface-100 p-3">
                      <div className="text-xl text-900 mb-2">Amazing Features</div>
                    </td>
                    {plans.map((plan) => (
                      <td className="surface-100 p-3 text-center" key={plan.billing_type}>
                        <div className="text-900 text-xl font-medium mb-3">{plan?.name}</div>
                      </td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(PLAN_FEATURES).map(([category, features]) => (
                    <>
                      <div className="text-900 font-bold text-xl mb-5 mt-5 ml-3">{category}</div>
                      {features.map((feature, index) => (
                        <tr key={index}>
                          <td className="p-3 text-900 font-medium border-bottom-1 border-200">
                            {feature.feature}
                          </td>
                          {feature.plans.map((plan, planIndex) => (
                            <td
                              key={planIndex}
                              className="text-center p-medium border-bottom-1 border-200"
                            >
                              <i
                                className={
                                  plan === 'Y'
                                    ? 'text-green-500 pi pi-check'
                                    : plan === 'N'
                                      ? 'text-500 pi pi-times'
                                      : 'text-900'
                                }
                              >
                                {plan !== 'Y' && plan !== 'N' ? plan : ''}
                              </i>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </Box>
        </>
      )}

      {/* Plan Recommendation Modal */}
      <PlanRecommendationModal
        visible={showPlanRecommendationModal}
        onHide={() => setShowPlanRecommendationModal(false)}
        plans={plans}
        ticketPacks={ticketPacks}
        subscription={subscription}
        siteData={siteData}
        isYearly={isYearly}
        couponCode={couponCode}
        onSelectPlan={(billingType) => {
          setShowPlanRecommendationModal(false);
          // Optionally scroll to the recommended plan card
          const planCard = document.querySelector(`[data-plan="${billingType}"]`);
          if (planCard) {
            planCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
      />
    </Box>
  );
};

function SubscriptionComponent({ subscription, plan, plans, siteData }) {
  // Handle Enterprise plans with custom pricing (null monthly_price)
  if (plan.monthly_price === null || plan.monthly_price === undefined) {
    return (
      <Box direction="vertical" gap={2} align="center">
        <Button
          priority="primary"
          size="large"
          className="w-full"
          onClick={() =>
            window.open('mailto:support@ticketspot.com?subject=Enterprise Plan Inquiry', '_blank')
          }
        >
          Contact Sales
        </Button>
      </Box>
    );
  }

  // Helper function to determine if the billing_type starts with 'enterprise' and extracts the numerical part
  const getEnterpriseValue = (billingType) => {
    if (!billingType) return null;
    if (billingType.startsWith('enterprise')) {
      const parts = billingType.split('-');
      return parts.length > 1 ? Number(parts[1]) : 0;
    }
    return null;
  };

  const currentEnterpriseValue = getEnterpriseValue(subscription?.billing_type);
  const targetEnterpriseValue = getEnterpriseValue(plan?.billing_type);

  // If both the current and target plans are enterprise plans, compare their numerical values
  if (currentEnterpriseValue !== null && targetEnterpriseValue !== null) {
    if (targetEnterpriseValue > currentEnterpriseValue) {
      return (
        <Box direction="vertical" gap={2} align="center">
          <Button type="submit" priority="primary" size="large" className="w-full">
            Select Plan
          </Button>
        </Box>
      );
    } else {
      // Current enterprise plan - no button needed (indicated by badge and border)
      return null;
    }
  }

  // Existing logic for non-enterprise plans
  const currentPlanIndex = plans.findIndex((p) => p.billing_type === subscription?.billing_type);
  const targetPlanIndex = plans.findIndex((p) => p.billing_type === plan.billing_type);

  // Check if site is Wix
  const isWixSite = siteData?.type === 'wix';

  // For Wix sites, don't allow downgrading (hide lower plans)
  if (isWixSite && targetPlanIndex < currentPlanIndex) {
    return null;
  }

  // For Wix sites: only show "Select Plan" for upgrades or different billing type
  // For non-Wix sites: allow selecting any plan (upgrade or downgrade)
  if (plan.billing_type !== subscription?.billing_type) {
    if (isWixSite) {
      // Wix: only allow if it's an upgrade
      if (targetPlanIndex > currentPlanIndex) {
        return (
          <Box direction="vertical" gap={2} align="center">
            <Button type="submit" priority="primary" size="large" className="w-full">
              Select Plan
            </Button>
          </Box>
        );
      }
    } else {
      // Non-Wix: allow selecting any plan (upgrade or downgrade)
      return (
        <Box direction="vertical" gap={2} align="center">
          <Button type="submit" priority="primary" size="large" className="w-full">
            Select Plan
          </Button>
        </Box>
      );
    }
  }

  // Current plan - no button needed (indicated by badge and border)
  return null;
}
